import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useRegister } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/api/authApi';
import { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';

const ROLE_LABELS: Record<string, string> = {
  RestaurantSeller: 'Bán / Sang nhượng quán',
  RestaurantBuyer: 'Sang lại / Mua lại quán',
  Landlord: 'Cho thuê mặt bằng',
  PropertySeeker: 'Tìm thuê mặt bằng',
  FurnitureSeller: 'Bán / Thanh lý thiết bị',
  FurnitureBuyer: 'Mua thiết bị / Đồ cũ',
  FranchiseSeeker: 'Tìm mua nhượng quyền',
  Franchiser: 'Nhượng quyền thương hiệu',
  JobSeeker: 'Tìm việc làm / Ứng viên',
  HeadhunterClient: 'Tuyển dụng nhân sự',
};

const schema = z
  .object({
    firstName: z.string().min(1, 'Nhập họ'),
    lastName: z.string().min(1, 'Nhập tên'),
    username: z.string().min(3, 'Tối thiểu 3 ký tự'),
    email: z.string().email('Email không hợp lệ'),
    password: z.string().min(6, 'Tối thiểu 6 ký tự'),
    confirmPassword: z.string(),
    roles: z.array(z.string()).min(1, 'Vui lòng chọn ít nhất 1 vai trò'),
    company: z.string().optional(),
    title: z.string().optional(),
  })
  .refine((d) => d.password === d.confirmPassword, { message: 'Mật khẩu không khớp', path: ['confirmPassword'] });

type FormData = z.infer<typeof schema>;

function Field({ label, error, children }: any) {
  return (
    <div className="space-y-1">
      <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--text-3)]">
        {label}
      </label>
      {children}
      {error && <p className="text-[11px] text-red-500">{error}</p>}
    </div>
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { mutate: register_, isPending } = useRegister();

  const { data: rolesData = [], isLoading: isLoadingRoles } = useQuery({
    queryKey: ['roles'],
    queryFn: authApi.getRoles,
  });

  const roleOptions = useMemo(() => {
    return rolesData
      .filter((r) => r.roleName.toLowerCase() !== 'admin')
      .map((r) => ({
        value: r.roleName,
        label: ROLE_LABELS[r.roleName] || r.roleName,
      }));
  }, [rolesData]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      roles: [],
    },
  });

  return (
    <div>
      <h1 className="font-display font-bold text-xl mb-0.5" style={{ color: 'var(--text-1)' }}>
        Tạo tài khoản
      </h1>
      <p className="text-xs mb-3" style={{ color: 'var(--text-3)' }}>
        Tham gia FNB HUB ngay hôm nay
      </p>

      <form
        onSubmit={handleSubmit((d) => {
          const { confirmPassword: _, ...payload } = d;

          const cleanPayload: any = {
            ...payload,
            roles: Array.isArray(payload.roles) ? payload.roles : (payload.roles ? [payload.roles] : []),
          };

          if (!cleanPayload.company || !cleanPayload.company.trim()) {
            delete cleanPayload.company;
          } else {
            cleanPayload.company = cleanPayload.company.trim();
          }

          if (!cleanPayload.title || !cleanPayload.title.trim()) {
            delete cleanPayload.title;
          } else {
            cleanPayload.title = cleanPayload.title.trim();
          }

          register_(cleanPayload, {
            onSuccess: (res: any) => {
              const email = res?.data?.email || res?.email || cleanPayload.email;
              navigate('/login', { state: { email } });
            },
          });
        })}
        className="space-y-2.5"
      >
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Họ *" error={errors.firstName?.message}>
            <input {...register('firstName')} placeholder="Nguyễn" className="fnb-input !h-9 text-sm" />
          </Field>
          <Field label="Tên *" error={errors.lastName?.message}>
            <input {...register('lastName')} placeholder="Văn A" className="fnb-input !h-9 text-sm" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Tên đăng nhập *" error={errors.username?.message}>
            <input {...register('username')} placeholder="my_username" className="fnb-input !h-9 text-sm" />
          </Field>
          <Field label="Email *" error={errors.email?.message}>
            <input {...register('email')} type="email" placeholder="you@example.com" className="fnb-input !h-9 text-sm" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Mật khẩu *" error={errors.password?.message}>
            <input {...register('password')} type="password" placeholder="Tối thiểu 6 ký tự" className="fnb-input !h-9 text-sm" />
          </Field>
          <Field label="Xác nhận mật khẩu *" error={errors.confirmPassword?.message}>
            <input {...register('confirmPassword')} type="password" placeholder="Nhập lại mật khẩu" className="fnb-input !h-9 text-sm" />
          </Field>
        </div>

        <Controller
          name="roles"
          control={control}
          render={({ field }) => (
            <div className="space-y-1 w-full">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--text-3)]">
                Bạn là ai? (Có thể chọn nhiều) *
              </label>
              <Autocomplete<{ value: string; label: string }, true>
                multiple
                options={roleOptions}
                getOptionLabel={(opt) => opt.label}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                value={roleOptions.filter((opt) => field.value?.includes(opt.value))}
                onChange={(_, newValue) => {
                  field.onChange(newValue.map((v) => v.value));
                }}
                disabled={isLoadingRoles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={isLoadingRoles ? 'Đang tải danh sách...' : field.value?.length ? '' : '-- Chọn vai trò --'}
                    error={!!errors.roles}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: 'var(--bg-1)',
                        minHeight: '38px',
                        padding: '2px 8px',
                        fontSize: '13px',
                        fontFamily: 'inherit',
                        color: 'var(--text-1)',
                        '& fieldset': { borderColor: errors.roles ? 'var(--destructive)' : 'var(--border-c)' },
                        '&:hover fieldset': { borderColor: errors.roles ? 'var(--destructive)' : 'var(--brand)' },
                        '&.Mui-focused fieldset': { borderColor: errors.roles ? 'var(--destructive)' : 'var(--brand)', borderWidth: '1.5px' },
                      },
                    }}
                  />
                )}
              />
              {errors.roles?.message && (
                <p className="text-[11px] text-red-500 mt-0.5">{errors.roles.message}</p>
              )}
            </div>
          )}
        />

        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Tên công ty" error={errors.company?.message}>
            <input {...register('company')} placeholder="Real Estate Co" className="fnb-input !h-9 text-sm" />
          </Field>
          <Field label="Chức danh" error={errors.title?.message}>
            <input {...register('title')} placeholder="Property Owner" className="fnb-input !h-9 text-sm" />
          </Field>
        </div>

        <Button type="submit" variant="brand" loading={isPending} className="w-full mt-1.5 h-10 text-sm font-bold rounded-xl">
          Tạo tài khoản <ArrowRight size={15} />
        </Button>
      </form>

      <div className="my-3 h-px" style={{ background: 'var(--border-c)' }} />
      <p className="text-center text-xs" style={{ color: 'var(--text-3)' }}>
        Đã có tài khoản?{' '}
        <Link to="/login" className="font-bold hover:underline" style={{ color: 'var(--brand)' }}>
          Đăng nhập
        </Link>
      </p>
    </div>
  );
}
